import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Button from '../../BaseComponents/Button';
import Loader from '../../BaseComponents/Loader';
import Modal from '../../BaseComponents/Modal';
import MultiSelect from '../../BaseComponents/MultiSelect';
import TableHeader from '../../BaseComponents/TableHeader';
import TableRowActionMenu from '../../BaseComponents/TableRowActionMenu';
import { getBodySystemsList } from '../../../api/body-systems';
import {
  getMriConditionsList,
  MriCondition,
  updateMriCondition,
} from '../../../api/mriRadiology';
import { setBodySystemsList, setBsCount } from '../../../store/appSlice';

const headerColumns = [
  { title: 'Condition', key: 'condition', minWidth: '220px', width: '220px', flex: 0 },
  { title: 'Radiologist System', key: 'radiologist_system', minWidth: '180px', width: '180px', flex: 0 },
  { title: 'Body Systems', key: 'body_systems', minWidth: '160px', width: '160px', flex: 0 },
  { title: 'Organ', key: 'organ', minWidth: '140px', width: '140px', flex: 0 },
  { title: 'Gender', key: 'gender', minWidth: '90px', width: '90px', flex: 0 },
  { title: 'Status', key: 'status', minWidth: '100px', width: '100px', flex: 0 },
  { title: '', key: 'actions', minWidth: '50px', width: '50px', flex: 0 },
];

interface FormValues {
  body_systems: { key: string; value: string }[];
}

const MriConditionsSection = () => {
  const dispatch = useDispatch();
  const [items, setItems] = useState<MriCondition[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [entityToEdit, setEntityToEdit] = useState<MriCondition | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [bodySystemOptions, setBodySystemOptions] = useState<{ key: string; value: string }[]>([]);

  const getInitialValues = (): FormValues => ({
    body_systems: (entityToEdit?.body_systems ?? []).map((bodySystem) => ({
      key: String(bodySystem.id),
      value: bodySystem.title,
    })),
  });

  const loadItems = async () => {
    try {
      setLoading(true);
      const response = await getMriConditionsList();
      setItems(response.items ?? []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load MRI conditions');
    } finally {
      setLoading(false);
    }
  };

  const refreshBodySystemsList = async () => {
    try {
      const response = await getBodySystemsList();
      const bodySystems = response.body_systems ?? [];
      dispatch(setBodySystemsList(bodySystems));
      dispatch(setBsCount(bodySystems.length));
    } catch (error) {
      console.error(error);
    }
  };

  const loadBodySystemOptions = async () => {
    try {
      const bodySystems = await getBodySystemsList();
      setBodySystemOptions(
        (bodySystems.body_systems ?? []).map((item) => ({ key: String(item.id), value: item.title }))
      );
    } catch (error) {
      console.error(error);
      toast.error('Failed to load body systems');
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleEdit = (entity: MriCondition) => {
    loadBodySystemOptions();
    setEntityToEdit(entity);
    setIsFormOpen(true);
  };

  const handleBodySystemsChange = (
    option: { key: string; value: string },
    currentBodySystems: { key: string; value: string }[],
    setFieldValue: (field: string, value: unknown) => void
  ) => {
    const existingIndex = currentBodySystems.findIndex((bodySystem) => bodySystem.key === option.key);
    const nextBodySystems =
      existingIndex >= 0
        ? currentBodySystems.filter((_, index) => index !== existingIndex)
        : [...currentBodySystems, option];
    setFieldValue('body_systems', nextBodySystems);
  };

  if (loading) return <Loader />;

  return (
    <div className='mri-conditions-section'>
      <div className='search-row-container'>
        <div className='search-and-filter-container'>
          <div className='count-container'>
            {items.length} {items.length === 1 ? 'MRI Condition' : 'MRI Conditions'}
          </div>
        </div>
      </div>

      <div className='scroll-container'>
        <div className='table-container mri-conditions-table'>
          <TableHeader headerColumns={headerColumns} gap='24px' />
          <div className='table-body'>
            {items.length > 0 ? (
              items.map((item) => (
                <div key={item.id} className='table-row mri-conditions-table-row'>
                  <div className='condition-description'>{item.condition_description}</div>
                  <div className='radiologist-system'>{item.radiologist_body_system_name}</div>
                  <div className='body-system'>
                    {item.body_systems?.length
                      ? item.body_systems.map((bodySystem) => bodySystem.title).join(', ')
                      : '-'}
                  </div>
                  <div className='organ'>{item.radiology_organ_name}</div>
                  <div className='gender'>{item.gender}</div>
                  <div className='status'>
                    <span className={`status-badge ${item.enabled ? 'enabled' : 'disabled'}`}>
                      {item.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className='actions'>
                    <TableRowActionMenu
                      options={[{ key: 'edit', value: 'Edit' }]}
                      id={item.id}
                      onActionMenuClick={(_id, action) => {
                        if (action === 'edit') handleEdit(item);
                      }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className='empty-state'>No MRI conditions found.</div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEntityToEdit(null);
        }}
        title='Edit MRI Condition'
        width='480px'
        wrapperClassName='mri-condition-edit-modal'
      >
        <Formik
          enableReinitialize
          key={`${entityToEdit?.id ?? 'edit'}-${bodySystemOptions.length}`}
          initialValues={getInitialValues()}
          onSubmit={async (values) => {
            if (!entityToEdit) {
              return;
            }

            if (!values.body_systems.length) {
              toast.error('Please select at least one body system');
              return;
            }

            try {
              setIsSaving(true);
              await updateMriCondition({
                id: entityToEdit.id,
                body_system_ids: values.body_systems.map((bodySystem) => Number(bodySystem.key)),
              });
              toast.success('MRI condition updated successfully');
              setIsFormOpen(false);
              setEntityToEdit(null);
              await Promise.all([loadItems(), refreshBodySystemsList()]);
            } catch (error) {
              console.error(error);
              toast.error('Failed to save MRI condition');
            } finally {
              setIsSaving(false);
            }
          }}
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <form onSubmit={handleSubmit} className='mri-condition-edit-form'>
              {entityToEdit && (
                <p className='mri-condition-edit-form__condition'>
                  {entityToEdit.condition_description}
                </p>
              )}

              <MultiSelect
                name='body_systems'
                title='Body Systems *'
                options={bodySystemOptions}
                selectedOptions={values.body_systems}
                onChange={(option) =>
                  handleBodySystemsChange(option, values.body_systems, setFieldValue)
                }
                placeholder='Select body system(s)'
                isError={false}
                useMenuPortal
                maxHeight='280px'
              />

              <div className='mri-condition-edit-actions'>
                <Button
                  text='Cancel'
                  type='button'
                  onClick={() => {
                    setIsFormOpen(false);
                    setEntityToEdit(null);
                  }}
                  backgroundColor='#ffffff'
                  color='#152735'
                />
                <Button
                  text='Save changes'
                  type='submit'
                  isDisabled={!values.body_systems.length}
                  isLoading={isSaving}
                />
              </div>
            </form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default MriConditionsSection;
